import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

/**
 * HelpModal - Credentials Management
 * Quick-reference popup explaining every section/action on this page.
 * Purely informational; no data mutation.
 */
const HelpModal = ({ isOpen, onClose, onStartTutorial }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Credentials Management — Quick Guide</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close help">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5 text-sm text-gray-700">
          <p>
            This page manages Platform User credentials (access key + secret) for the current project.
            Each row is a <strong>physical user</strong> (a credential) linked to a logical user (POC, IP whitelist).
          </p>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Add New Credential</h3>
            <p>Creates a brand-new credential for the selected project: description, quota, alert threshold, point(s) of contact, and the APIs it should have access to (choose from the available permission templates — Certificate Provisioning is an exclusive choice and can&apos;t be combined with other APIs). Access key + secret are shown once on success — copy, download the CSV, or check the documentation link, they can&apos;t be viewed again.</p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Filters &amp; Favorites</h3>
            <p>Use &quot;Show Filters&quot; to narrow the list by description, project, quota, threshold, point of contact or expiration status. Click the ★ next to a credential to pin it to the top of the list (stored locally in your browser). Click the &quot;Expiration&quot; column header to sort by how soon each credential expires.</p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Expiration status</h3>
            <p>Each credential shows an expiration indicator computed from its expiration date: <strong>OK</strong> (healthy), <strong>Expiring soon</strong> (within 30 days), <strong>Expired</strong>, or <strong>No expiry</strong> (no expiration date set). This is shown in both the list and the credential&apos;s details.</p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Row actions</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong>View &amp; Edit Details</strong> — edit description, alert threshold, point(s) of contact, IP whitelist, and the project(s)/product(s) (&quot;Products&quot;) the credential belongs to.
                Changing Products triggers an IAM policy update on the platform — a change request id is shown with a &quot;Check status&quot; button to track propagation, and the change is audit-logged.
                Also shows the credential&apos;s access key(s) status (Active/Inactive) and last-used date, plus a &quot;Change History&quot; (audit log) tab.
                When a credential has two access keys (e.g. right after a rotation), you can confirm the new one works and remove the old one
                directly from this screen — you&apos;ll be warned if the key you&apos;re removing was used within the last 24 hours.
                If a credential has no active access key, an &quot;Enable&quot; banner lets you re-enable it. A &quot;Permissions&quot; section shows
                the APIs currently granted and lets you add or remove them (Certificate Provisioning remains an exclusive choice).
              </li>
              <li>
                <strong>Increase Quota</strong> — raise the certificate quota for this credential.
              </li>
              <li>
                <strong>Rotate Credentials</strong> — generates a brand-new access key + secret for this credential (365-day policy).
                The new secret is shown once (copy/download CSV). AWS allows at most 2 keys per credential —
                if rotation fails with &quot;already has 2 keys&quot;, remove the old key first from View &amp; Edit Details (see above) before rotating again.
              </li>
              <li>
                <strong>Permanently Delete Credential</strong> — two-step deletion: first cleans up access keys, then deletes the physical user.
                <span className="block text-amber-700 mt-1">Known issue: if the credential has 2 access keys (e.g. right after a rotation), the cleanup step may only remove one key and deletion can fail with &quot;access keys associated&quot;. Retry, or clean up keys manually from the details view first.</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Security notes</h3>
            <p>Secret access keys are never stored by the Portal after you close the show-once screen — if lost, you must rotate again to get a new one.</p>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-between">
          {onStartTutorial && (
            <button
              onClick={onStartTutorial}
              className="px-4 py-2 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md focus:outline-none"
            >
              Start Guided Tutorial
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
